/* eslint-disable @typescript-eslint/no-unnecessary-condition -- defensive reads of optional projection fields */
import { useState } from 'preact/hooks';
import { useStore } from '../store';

/** Pedigree & pregnancy view (#332 ui.mgmt.breeding). Read-only management
 *  view: pregnancies with due countdown + expected coat odds, and a
 *  pedigree browser (sire/dam, grandparents). The mate PICKER action flow
 *  is UI-Shell's PairingSheet (#321) - no pair.request dispatch here.
 *  Reads sim.breeding via a narrow local cast until the bridge field lands. */

interface BreedingProjection {
  pedigree: Record<string, { sire: string | null; dam: string | null }>;
  pregnancies: Array<{ damId: string; sireId: string; dueDay: number }>;
  pairs?: Array<{ sireId: string; damId: string; inbreedingBp: number; odds: Array<{ coat: string; bp: number }> }>;
}

const fmtCoat = (c: string) => c.replace(/_/g, ' ');

export function PedigreePanel() {
  const { sim } = useStore();
  const breeding = (sim as unknown as { breeding?: BreedingProjection }).breeding;
  const [openId, setOpenId] = useState<string | null>(null);
  const day = Math.floor(sim.tick / 24);
  const nameOf = (id: string | null | undefined) => {
    if (!id) return 'Unknown';
    const a = sim.animals.find((x) => x.id === id);
    return a ? a.name : id;
  };
  const speciesOf = (id: string | null | undefined) => sim.animals.find((x) => x.id === id)?.species;

  if (!breeding) {
    return (
      <div style="padding:6px 14px 16px">
        <div class="eyebrow eyebrow-rule">Breeding</div>
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:8px">
          Pregnancies and pedigrees appear once the breeding projection lands.
        </p>
      </div>
    );
  }

  const { pedigree, pregnancies, pairs } = breeding;
  const pedIds = Object.keys(pedigree).filter((id) => pedigree[id].sire || pedigree[id].dam);
  const expectedFor = (damId: string, sireId: string) => {
    const pair = pairs?.find((p) => p.damId === damId && p.sireId === sireId);
    if (!pair || pair.odds.length === 0) return null;
    return pair.odds.slice(0, 2).map((o) => `${Math.round(o.bp / 100)}% ${fmtCoat(o.coat)}`).join(', ');
  };

  return (
    <div style="padding:6px 14px 16px">
      <div class="eyebrow eyebrow-rule">Breeding</div>

      <div class="row-label" style="margin-top:12px">Pregnancies</div>
      {pregnancies.length === 0 && (
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:6px">No pregnancies right now. Pair mates from the herd sheet.</p>
      )}
      {pregnancies.map((pr, i) => {
        const left = pr.dueDay - day;
        const expected = expectedFor(pr.damId, pr.sireId);
        return (
          <div key={`${pr.damId}-${i}`} class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
            <div class="row-value" style="font-size:14px">{nameOf(pr.damId)} <span style="font-weight:400;color:var(--ui-ink-soft)">× {nameOf(pr.sireId)}</span></div>
            <div class="row-label" style="margin-top:3px">
              {speciesOf(pr.damId) ?? 'unknown species'} · {left > 0 ? `due in ${left} day${left === 1 ? '' : 's'}` : 'due any moment'}
            </div>
            {expected && <div class="row-label" style="margin-top:2px;text-transform:none;letter-spacing:0.01em">Expected: {expected}</div>}
          </div>
        );
      })}

      <div class="row-label" style="margin-top:16px">Pedigrees</div>
      {pedIds.length === 0 && (
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:6px">No recorded parentage yet - newborns start the family book.</p>
      )}
      {pedIds.map((id) => {
        const rec = pedigree[id];
        const a = sim.animals.find((x) => x.id === id);
        const open = openId === id;
        return (
          <div key={id} class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px;cursor:pointer" onClick={() => { setOpenId(open ? null : id); }}>
            <div class="row" style="margin:0;border:none;padding:0">
              <span class="row-main">
                <div class="row-value" style="font-size:14px">{a ? a.name : id}</div>
                <div class="row-label">{a ? `${a.species.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} · ${a.sex}` : 'no longer on the reserve'}</div>
              </span>
              <span class="row-meta">{open ? '▾' : '▸'}</span>
            </div>
            {open && (
              <div style="margin-top:8px;border-top:var(--ui-border);padding-top:8px;font-size:13px">
                <div><span class="row-label" style="display:inline">Sire</span> {nameOf(rec.sire)}{rec.sire && speciesOf(rec.sire) ? ` (${speciesOf(rec.sire)})` : ''}</div>
                <div style="margin-top:4px"><span class="row-label" style="display:inline">Dam</span> {nameOf(rec.dam)}{rec.dam && speciesOf(rec.dam) ? ` (${speciesOf(rec.dam)})` : ''}</div>
                {(rec.sire && pedigree[rec.sire]) || (rec.dam && pedigree[rec.dam]) ? (
                  <div style="margin-top:6px;color:var(--ui-ink-soft)">
                    {rec.sire && pedigree[rec.sire] ? `Sire's line: ${nameOf(pedigree[rec.sire].sire)} × ${nameOf(pedigree[rec.sire].dam)}` : ''}
                    {rec.sire && pedigree[rec.sire] && rec.dam && pedigree[rec.dam] ? <br /> : null}
                    {rec.dam && pedigree[rec.dam] ? `Dam's line: ${nameOf(pedigree[rec.dam].sire)} × ${nameOf(pedigree[rec.dam].dam)}` : ''}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
